import {
  DeletePasscodeListMembers,
  GenerateMagicLinksForEvent,
  HandleDownloadCSV,
  InvalidateMagicLinksForEvent,
} from '@/connection/audiences/passcode-lists';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectPasscodeListMemberCount,
  selectPasscodeListMembers,
  selectPasscodeListMembersLoading,
} from '@/store/slices/passcode-lists/selectors';
import {
  DownloadCSVStringValues,
  DownloadStringValues,
  PASSCODE_LIST_MEMBER_PAGE_SIZE,
  PasscodeList,
  PasscodeListMember,
} from '@/types/registration.types';
import Container from '@ui/container/container';
import { useEffect, useState } from 'react';
import PasscodeListMemberTableHeader from './passcode-list-member-table-header';
import PasscodeListMemberTableRow from './passcode-list-member-table-row';
import { useSearchParams } from 'react-router-dom';
import { PASSCODE_LIST_MEMBER_ORDER_OPTIONS, PasscodeListMemberSort } from '../../types';
import { downloadCsv, getExportDate, PasscodeListMemberOptionsEnumToSortOrder } from '../../utils';
import TableBody from '@layouts/layout-table/layout-table-body';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { getWorkingProjectUuid, selectProjectEventId } from '@/store/slices/project/selectors';
import { fetchPasscodeListMemberCount, resetPasscodeListAndMembers } from '@/store/slices/passcode-lists';
import Table from '@layouts/layout-table/layout-table';
import OptionalComponent from '@ui/optional-component/optional-component';
import Paging from '@ui/paging/paging';
import Text from '@/general-ui/text/text/text';
import AudiencesTableSelectedMemberActions from '../audiences-table-selected-member-actions';
import toastTrigger from '@ui/toast/toast-trigger';
import ConfirmResetMagicLinksModal from './modal-confirm-reset-magic-links';
import { useLoadPasscodeListMembers } from '../../hooks/use-load-passcode-list-members';
import TableListSparseSkeletonLoader from '@ui/skeleton-loader/custom/table-list-sparse-skeleton-loader';

type PasscodeListMemberListProps = {
  passcodeList: PasscodeList;
  reloadMembers?: () => void;
};

const PasscodeListMemberList = ({ passcodeList, reloadMembers }: PasscodeListMemberListProps) => {
  const dispatch = useAppDispatch();

  const passcodeListMembers = useAppSelector(selectPasscodeListMembers);
  const channel = useAppSelector(getActiveChannel);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const projectId = useAppSelector(selectProjectEventId);
  const count = useAppSelector(selectPasscodeListMemberCount);
  const passcodeListMembersLoading = useAppSelector(selectPasscodeListMembersLoading);

  useLoadPasscodeListMembers(
    channel || 0,
    passcodeList.passcode_list || 0,
    projectId || 0,
    projectUuid || '',
    PASSCODE_LIST_MEMBER_PAGE_SIZE,
    true
  );

  const [params, setParams] = useSearchParams();
  const paramsObj = Object.fromEntries(params.entries());
  const [sort, setSort] = useState<PasscodeListMemberSort>();
  const [, setPage] = useState(1);
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [confirmResetMagicLinksOpen, setConfirmResetMagicLinksOpen] = useState(false);

  useEffect(() => {
    //when the component unmounts, clear the passcode list member data
    return () => {
      dispatch(resetPasscodeListAndMembers());
    };
  }, [dispatch]);

  // reload params from the url in case user has refreshed
  useEffect(() => {
    if (paramsObj.sort) {
      setSort(PasscodeListMemberOptionsEnumToSortOrder(Number(paramsObj.sort) as PASSCODE_LIST_MEMBER_ORDER_OPTIONS));
    } else {
      setSort(0 as unknown as PasscodeListMemberSort);
    }
  }, [paramsObj.sort]);

  useEffect(() => {
    if (paramsObj.search) {
      setPage(1);
    }
    if (passcodeList.passcode_list) {
      dispatch(
        fetchPasscodeListMemberCount({ passcodeListId: passcodeList.passcode_list, search: paramsObj.search || '' })
      );
    }
  }, [paramsObj.search, passcodeList.passcode_list, dispatch]);

  const handleIsSelected = (passcodeListMemberId: number) => {
    if (selectedMembers.includes(passcodeListMemberId)) {
      setSelectedMembers(selectedMembers.filter((id) => id !== passcodeListMemberId));
    } else {
      setSelectedMembers([...selectedMembers, passcodeListMemberId]);
    }
  };

  const handleSelectAll = () => {
    if (passcodeListMembers.map((member) => member.passcode_list_member).every((id) => selectedMembers.includes(id))) {
      //all members are already selected, so deselect all
      setSelectedMembers([]);
    } else {
      //select all members
      setSelectedMembers(passcodeListMembers.map((member) => member.passcode_list_member));
    }
  };

  const handleRegenerateMagicLinks = async () => {
    if (channel && passcodeList.passcode_list && projectUuid && selectedMembers.length) {
      const selectedPasscodeListMembers = passcodeListMembers.filter((member) =>
        selectedMembers.includes(member.passcode_list_member)
      );
      await GenerateMagicLinksForEvent(
        channel,
        passcodeList.passcode_list,
        projectUuid,
        passcodeList.type,
        selectedPasscodeListMembers
      );

      if (reloadMembers) {
        reloadMembers();
      }

      setConfirmResetMagicLinksOpen(false);
    }
  };

  const handleDownloadPasscodeList = async (downloadType: DownloadStringValues, passcodeListMemberId?: number) => {
    if (!passcodeList.member_count) {
      toastTrigger(
        <div className="S1">
          <div>Your audience list does not have members yet.</div>
        </div>,
        {
          type: 'error',
          autoClose: 10000,
        }
      );
    }

    let membersToGet: PasscodeListMember[] = [];

    if (passcodeListMemberId) {
      membersToGet = passcodeListMembers.filter((member) => member.passcode_list_member === passcodeListMemberId);
    } else {
      membersToGet = passcodeListMembers.filter((member) => selectedMembers.includes(member.passcode_list_member));
    }

    try {
      const response = await HandleDownloadCSV(
        passcodeList,
        downloadType,
        membersToGet,
        projectUuid || null,
        passcodeList.dma_id
      );

      if (!response.ok) {
        toastTrigger(
          <div className="S1">
            <div>There was an error downloading the magic links.</div>
          </div>,
          {
            type: 'error',
            autoClose: 10000,
          }
        );
        return;
      }

      downloadCsv(response, `${passcodeList.name}-${getExportDate()}`);
    } catch (e) {
      console.error('Error downloading magic links:', e);
      toastTrigger(
        <div className="S1">
          <div>Download failed</div>
          <div>Your audience list failed to generate</div>
        </div>,
        {
          type: 'error',
          autoClose: 10000,
        }
      );
    }
  };

  const handleRemoveSelectedPasscodeListMembers = async () => {
    if (passcodeList.passcode_list && projectUuid && selectedMembers.length) {
      try {
        await InvalidateMagicLinksForEvent(passcodeList.passcode_list, projectUuid, selectedMembers);
      } catch (e) {
        console.error('Error invalidating magic links', e);
        toastTrigger(
          <div className="S1">
            <div>There was a problem invalidating magic links for the members.</div>
          </div>,
          {
            type: 'error',
            autoClose: 10000,
          }
        );
        return;
      }

      try {
        await DeletePasscodeListMembers(selectedMembers);
      } catch (e) {
        console.error('Error deleting passcode list members', e);
        toastTrigger(
          <div className="S1">
            <div>There was a problem removing members from the list.</div>
          </div>,
          {
            type: 'error',
            autoClose: 5000,
          }
        );

        return;
      }

      toastTrigger(
        <div className="S1">
          <div>{`${selectedMembers.length} member${selectedMembers.length > 1 ? 's' : ''} removed from list`}</div>
        </div>,
        {
          type: 'success',
          autoClose: 5000,
        }
      );

      setSelectedMembers([]);

      if (reloadMembers) {
        reloadMembers();
      }
    }
  };

  const getTableContentsHeight = () => {
    let heightToRemove = 240; //height in px of the app header and the passcode list header
    const totalPages = Math.ceil(count / PASSCODE_LIST_MEMBER_PAGE_SIZE);
    const selectedMembersShowing = selectedMembers.length > 0;

    //selected member actions will be showing
    if (selectedMembersShowing) {
      heightToRemove += 42; // height of the selected members actions
    }

    //pagenation buttons will be showing
    if (totalPages > 1) {
      heightToRemove += 32; // height of the paging component
    }

    return `calc(100vh - ${heightToRemove}px)`;
  };

  return (
    <>
      <Container className="position-relative" minHeight="calc(100vh - 600px)">
        <Container
          opacity={passcodeListMembersLoading ? 1 : 0}
          className="flex ai-flex-start position-absolute z-10 top-0 left-0 right-0 bottom-0"
          style={{
            visibility: passcodeListMembersLoading ? 'visible' : 'hidden',
            transition: 'opacity 300ms ease-in-out',
            marginTop: 10,
          }}
        >
          <TableListSparseSkeletonLoader />
        </Container>
        <Container
          className="flex-column jc-flex-start padding-t-16"
          width="100%"
          height="100%"
          opacity={passcodeListMembersLoading ? 0 : 1}
          style={{
            transition: 'opacity 300ms ease-in-out',
            pointerEvents: passcodeListMembersLoading ? 'none' : 'auto',
            visibility: passcodeListMembersLoading ? 'hidden' : 'visible',
          }}
        >
          <OptionalComponent display={passcodeListMembers.length > 0}>
            <div
              style={{
                visibility: selectedMembers.length > 0 ? 'visible' : 'hidden',
                maxHeight: selectedMembers.length > 0 ? '100px' : 0,
                overflow: 'hidden',
                transition: 'max-height 500ms ease-in-out',
              }}
            >
              <AudiencesTableSelectedMemberActions
                selectedMembers={selectedMembers}
                passcodeList={passcodeList}
                clearSelectedMembers={() => setSelectedMembers([])}
                removeSelectedPasscodeListMembers={handleRemoveSelectedPasscodeListMembers}
                regenerateMagicLinks={() => setConfirmResetMagicLinksOpen(true)}
                downloadMagicLinks={() => handleDownloadPasscodeList(DownloadCSVStringValues.SPECIFIC_MAGIC)}
                downloadInfo={() => handleDownloadPasscodeList(DownloadCSVStringValues.SPECIFIC)}
              />
            </div>
            <Container className="margin-b-16" height={getTableContentsHeight()} style={{ overflowY: 'auto' }}>
              <Table>
                <PasscodeListMemberTableHeader
                  passcodeList={passcodeList}
                  sort={sort}
                  setSort={setSort}
                  isAllSelected={passcodeListMembers
                    .map((member) => member.passcode_list_member)
                    .every((id) => selectedMembers.includes(id))}
                  selectAll={handleSelectAll}
                />
                <TableBody>
                  {passcodeListMembers.map((passcodeListMember) => {
                    return (
                      <PasscodeListMemberTableRow
                        key={passcodeListMember.passcode_list_member}
                        passcodeListMember={passcodeListMember}
                        passcodeList={passcodeList}
                        setIsSelected={() => handleIsSelected(passcodeListMember.passcode_list_member)}
                        isSelected={selectedMembers.includes(passcodeListMember.passcode_list_member)}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </Container>
          </OptionalComponent>

          <OptionalComponent display={passcodeListMembers.length === 0}>
            <OptionalComponent display={!!paramsObj.search}>
              <div className="flex-column jc-center ai-center padding-v-100">
                <Text tag="h2">No matches found for your search.</Text>
              </div>
            </OptionalComponent>
            <OptionalComponent display={!paramsObj.search}>
              <div className="flex-column jc-center ai-center padding-v-100">
                <Text tag="h2">{passcodeList.name} has no members.</Text>
              </div>
            </OptionalComponent>
          </OptionalComponent>

          <OptionalComponent display={count > PASSCODE_LIST_MEMBER_PAGE_SIZE && passcodeListMembers.length > 0}>
            <Paging
              totalPages={Math.ceil(count / PASSCODE_LIST_MEMBER_PAGE_SIZE)}
              page={paramsObj.page ? parseInt(paramsObj.page) : 1}
              setPage={(num) => {
                setParams((prev) => {
                  const currParams = Object.fromEntries(prev.entries());
                  return {
                    ...currParams,
                    page: num.toString(),
                  };
                });
              }}
            />
          </OptionalComponent>
        </Container>
      </Container>

      <ConfirmResetMagicLinksModal
        open={confirmResetMagicLinksOpen}
        onClose={() => setConfirmResetMagicLinksOpen(false)}
        onResetMagicLinks={() => handleRegenerateMagicLinks()}
      />
    </>
  );
};

export default PasscodeListMemberList;
